/**
 * Help-centre index, assembled at build time.
 *
 * Every `.md` under this directory is read raw via `import.meta.glob` and run through the
 * shared parser in `scripts/help/parse.mjs` — the same parser the Node-side validator uses.
 *
 * ⚠️ The throws in this module do NOT gate the build. `import.meta.glob` inlines file
 * CONTENT at build time, but this module's code only EXECUTES in the browser, so a
 * malformed article would reach the reader as a white screen. The build gate is the
 * `validate-help` Vite plugin (`scripts/help/validate.mjs`), wired up in `vite.config.ts`.
 * Treat what happens here as defence in depth, not the safety net.
 *
 * There is no runtime fetch and no CMS: content ships inside the bundle. Audience
 * filtering, search and navigation are pure functions over `articles` in `./model` — keep
 * this module to the glob + parse so it stays the ONLY file Jest cannot load directly
 * (`import.meta` — tests mock this module via `src/content/help/__mocks__/index.ts`).
 */

import { parseArticle, crossCheck } from "../../../scripts/help/parse.mjs";
import { categories } from "./categories";
import type { DocArticle } from "./types";

export type { DocArticle, DocCategory, DocHeading, DocType, DocAudience } from "./types";
export type { CategoryDef } from "./categories";
export { categories as categoryDefs } from "./categories";

const categorySlugs = categories.map((c) => c.slug);

const rawFiles = import.meta.glob("./**/*.md", {
  query: "?raw",
  eager: true,
  import: "default",
}) as Record<string, string>;

export const articles: DocArticle[] = Object.entries(rawFiles)
  .map(([file, raw]) => parseArticle(file, raw, categorySlugs))
  .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));

{
  const errors = crossCheck(articles);
  if (errors.length) throw new Error(`[help] ${errors.join("; ")}`);
}
